import { reactive, toRefs, onMounted, onUnmounted, watch } from 'vue'
import { useAuth } from './auth/useAuth.js'
import { api } from '../services/api.js'

/**
 * Composable générique de comptage avec polling + rafraîchissement sur event.
 *
 * Factorise le squelette commun à useFeedbackPendingCount et usePendingVeilleCount :
 * ref(s) d'état, chargement gardé par l'auth, auto-refresh périodique, écoute d'un
 * CustomEvent global pour rafraîchir immédiatement après une action côté UI, et
 * nettoyage au démontage.
 *
 * @param {object} opts
 * @param {string} opts.endpoint        URL à interroger (GET, credentials include)
 * @param {string} opts.eventName       nom du CustomEvent déclenchant un reload
 * @param {object} opts.initialState    état initial (un ou plusieurs compteurs)
 * @param {(data:any)=>object} opts.mapResponse  mappe la réponse API vers l'état
 * @param {(user:any)=>boolean} [opts.gate]  condition de chargement (défaut: user connecté)
 * @param {number} [opts.intervalMs=60000]   période de polling
 * @returns {{ state: object, reload: () => Promise<void> }}
 */
export function usePollingCount({ endpoint, eventName, initialState, mapResponse, gate, intervalMs = 60000 }) {
  const { user: currentUser } = useAuth()
  const state = reactive({ ...initialState })
  let pollInterval = null

  const allowed = () => (gate ? gate(currentUser.value) : !!currentUser.value?.id)

  function reset() {
    Object.assign(state, initialState)
  }

  async function reload() {
    if (!allowed()) {
      reset()
      return
    }
    try {
      const data = await api.get(endpoint)
      Object.assign(state, mapResponse(data))
    } catch {
      // silencieux : un compteur de badge n'est pas critique
    }
  }

  function handleUpdate() {
    reload()
  }

  watch(
    () => (gate ? gate(currentUser.value) : currentUser.value?.id),
    (v) => {
      if (v) reload()
      else reset()
    }
  )

  onMounted(() => {
    if (allowed()) reload()
    pollInterval = setInterval(reload, intervalMs)
    window.addEventListener(eventName, handleUpdate)
  })

  onUnmounted(() => {
    if (pollInterval) clearInterval(pollInterval)
    window.removeEventListener(eventName, handleUpdate)
  })

  return { state, toRefs: () => toRefs(state), reload }
}

/** Déclenche le CustomEvent global écouté par les compteurs correspondants. */
export function notifyCountUpdated(eventName) {
  window.dispatchEvent(new CustomEvent(eventName))
}
